// ./src/utils/api/SignupRecordService.ts
//
// Guest sign-ups for signup-list ITEMS (who's bringing/doing what, how much + a note).
// OpResult-native, event-scoped. A signup-item VIEW over the unified `event_guest_signup`
// table (which also backs POI / accommodation / travel sign-ups): this service always
// scopes to targetKind='signup_item' and maps the item pointer to/from the table's generic
// `target_id` (exposed here as `signupItemId` so callers never see the polymorphic columns).
// Talks to the eventGuestSignup* GraphQL operations. Full single-record CRUD plus a bulk
// status change / remove.
//
// A guest may have SEVERAL records for the same item (cola ×50 on one, beers ×30 on
// another), so there's no per-(item,guest) uniqueness — the FE just lists all records.
// Host/crew (account users) enter these today; the SAME table backs the guest's own
// sign-up on the public event website (enteredByKind='guest'/'visitor'). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the server GUEST_SIGNUP_ENTERED_BY_KIND boundary)
// =============================================================================

export const SIGNUP_ENTERED_BY_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VISITOR: "visitor",
} as const;

export type SignupEnteredByKind = (typeof SIGNUP_ENTERED_BY_KIND)[keyof typeof SIGNUP_ENTERED_BY_KIND];

// This service always operates on signup-list items in the unified table for its CRUD reads.
const SIGNUP_ITEM_TARGET_KIND = "signup_item";

/**
 * The target kinds the guest-signup REPORT can be asked for — mirrors GUEST_SIGNUP_REPORT_KINDS.
 *
 * ⚠ The report is one parameterized server aggregate over the unified table, so all three kinds
 * come through the SAME query. Only `targetKind` changes; everything else on this page is shared.
 */
export const SIGNUP_REPORT_KIND = {
  SIGNUP_ITEM: "signup_item",
  POI: "poi",
  ACCOMMODATION: "accommodation",
} as const;

/**
 * What a kind's report COUNTS — mirrors SIGNUP_REPORT_MEASURE on the server.
 *
 * ⚠ Read it; do not infer it from the numbers. A POI or accommodation sign-up carries NO quantity
 * at all, so its `claimedQuantity` is 0 on every row — which is indistinguishable from "nobody has
 * claimed anything" if all the page has is the figures.
 */
export const SIGNUP_REPORT_MEASURE = {
  QUANTITY: "quantity",
  PARTICIPATION: "participation",
} as const;

// =============================================================================
// GraphQL
// =============================================================================

// `signupItemId: targetId` — alias the unified table's polymorphic target back to the
// item pointer this service exposes, so consumers keep reading `record.signupItemId`.
const RECORD_FIELDS = `
  id
  eventId
  accountId
  signupItemId: targetId
  eventGuestId
  quantity
  note
  enteredByKind
  signupOrderId
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const RECORD_LIST_QUERY = gql`
  query EventGuestSignupList($filter: EventGuestSignupFilter, $params: PaginationAndSorting) {
    eventGuestSignupList(filter: $filter, params: $params) {
      code
      data { ${RECORD_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RECORD_GET_QUERY = gql`
  query EventGuestSignupGet($id: ID) {
    eventGuestSignupGet(id: $id) {
      code
      data { ${RECORD_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RECORD_GET_MANY_QUERY = gql`
  query EventGuestSignupGetMany($ids: [ID]) {
    eventGuestSignupGetMany(ids: $ids) {
      code
      data { ${RECORD_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RECORD_CREATE_MUTATION = gql`
  mutation EventGuestSignupCreate($params: EventGuestSignupInput) {
    eventGuestSignupCreate(params: $params) {
      code
      data { ${RECORD_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RECORD_UPDATE_MUTATION = gql`
  mutation EventGuestSignupUpdate($where: EventGuestSignupWhereInput, $params: EventGuestSignupInput) {
    eventGuestSignupUpdate(where: $where, params: $params) {
      code
      data { ${RECORD_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const RECORD_REMOVE_MUTATION = gql`
  mutation EventGuestSignupRemove($where: EventGuestSignupWhereInput) {
    eventGuestSignupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (the signup-item view; `signupItemId` maps to the table's target_id)
// =============================================================================

export interface SignupRecord {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  signupItemId?: string | null;
  eventGuestId?: string | null;
  quantity?: number | null;
  note?: string | null;
  /** Provenance: user | crew | guest | visitor. */
  enteredByKind?: string | null;
  /** #149 — the order a guest PAID for this sign-up with online; null = not paid online. Its quantity cannot change. */
  signupOrderId?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SignupRecordInput {
  eventId?: string | null;
  signupItemId?: string | null;
  eventGuestId?: string | null;
  quantity?: number | null;
  note?: string | null;
  enteredByKind?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface SignupRecordBulkInput {
  status?: number | null;
}

export interface SignupRecordFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  signupItemId?: string[] | null;
  eventGuestId?: string[] | null;
  status?: number[] | null;
}

export interface SignupRecordWhereInput {
  id?: string | string[];
}

// Map the service's item-scoped filter onto the unified table's generic filter:
// signup-item scope + signupItemId → targetId.
const toApiFilter = (filter?: SignupRecordFilter): Record<string, unknown> => {
  const { signupItemId, ...rest } = filter || {};
  return {
    ...rest,
    targetKind: [SIGNUP_ITEM_TARGET_KIND],
    ...(signupItemId && signupItemId.length > 0 ? { targetId: signupItemId } : {}),
  };
};

// Map an item-scoped create/update onto the generic input: signupItemId → target_kind + target_id.
const toApiInput = (params: SignupRecordInput): Record<string, unknown> => {
  const { signupItemId, ...rest } = params;
  return {
    ...rest,
    ...(signupItemId ? { targetKind: SIGNUP_ITEM_TARGET_KIND, targetId: signupItemId } : {}),
  };
};

// ── The signup-list report (#44) ─────────────────────────────────────────────
// ONE server aggregate behind all THREE report pages. The server groups the unified table by
// (target_kind, target_id), so signup lists (#44), POIs (#46) and accommodations (#47) all read
// this one query — only `targetKind` changes.
const SIGNUP_STATS_QUERY = gql`
  query EventGuestSignupStats($params: EventGuestSignupStatsInput) {
    eventGuestSignupStats(params: $params) {
      code
      data {
        eventId
        targetKind
        audience
        includeEmptyTargets
        audienceGuests
        audienceHeads
        claimantGuests
        targetCount
        offeredCount
        untouchedCount
        fullCount
        unlistedCount
        totalOffered
        totalClaimed
        totalSignups
        claimsTruncated
        measure
        priceBasis
        hasStayDates
        isUpvotable
        totalHeads
        totalUpvotes
        totalNights
        unplaceableStays
        doubleBookedGuests
        peakNightDate
        peakNightHeads
        nightOccupancy { date stays heads }
        hasPricedItems
        totalsByCurrency { currencyCode amount }
        participation
        includeGuestRoster
        guestsSignedUp
        guestsNotSignedUp
        guestRosterTruncated
        guests {
          eventGuestId
          guestName
          email
          phone
          rsvpStatus
          partySize
          signupCount
          claimedQuantity
          amountsByCurrency { currencyCode amount }
          items { targetId name quantity amount currencyCode startsOn endsOn nights note claimedAt }
        }
        targets {
          targetId
          name
          categoryId
          quantityOffered
          maxQuantityPerGuest
          claimedQuantity
          signupCount
          guestCount
          remainingQuantity
          isFull
          isUntouched
          orderNo
          status
          isOffered
          price
          currencyCode
          priceUnit
          claimedAmount
          claimedHeads
          upvoteCount
          claimedNights
          unplaceableStays
          streetAddress
          distanceMeters
          groupBookingCode
          claims {
            eventGuestSignupId
            eventGuestId
            guestName
            rsvpStatus
            quantity
            note
            enteredByKind
            claimedAt
            startsOn
            endsOn
            nights
            amount
          }
        }
      }
      errors { name errors warnings }
    }
  }
`;

// The audience and what it sends live in a pure module (the report page, its print sheet and the guards share it).
export { SIGNUP_REPORT_AUDIENCE, signupReportCountParams } from './signupReportCount';
import { SIGNUP_REPORT_AUDIENCE, signupReportCountParams } from './signupReportCount';

/** One guest's claim on a signup item. */
/** Which guests the by-guest roster lists — mirrors SIGNUP_REPORT_PARTICIPATION on the server. */
export const SIGNUP_REPORT_PARTICIPATION = {
  ALL: "all",
  SIGNED_UP: "signed_up",
  NOT_SIGNED_UP: "not_signed_up",
} as const;

/** A money total for ONE currency. ⚠ Never add these together — see the server's addCurrencyAmount. */
export interface SignupAmount {
  currencyCode?: string | null;
  amount?: number | null;
}

export interface SignupGuestItem {
  targetId?: string | null;
  name?: string | null;
  quantity?: number | null;
  amount?: number | null;
  currencyCode?: string | null;
  /** The stay period (accommodations), as YYYY-MM-DD. Null on every other kind. */
  startsOn?: string | null;
  endsOn?: string | null;
  /** Nights the stay covers. ⚠ 0 means the dates on file could not be placed on a calendar. */
  nights?: number | null;
  note?: string | null;
  claimedAt?: string | null;
}

/** One guest and everything they signed up for. `signupCount: 0` = on the chase list. */
export interface SignupGuest {
  eventGuestId?: string | null;
  guestName?: string | null;
  email?: string | null;
  phone?: string | null;
  rsvpStatus?: string | null;
  partySize?: number | null;
  signupCount?: number | null;
  claimedQuantity?: number | null;
  amountsByCurrency?: SignupAmount[] | null;
  items?: SignupGuestItem[] | null;
}

export interface SignupClaim {
  eventGuestSignupId?: string | null;
  eventGuestId?: string | null;
  guestName?: string | null;
  rsvpStatus?: string | null;
  /** Null means they put their name down without an amount. */
  quantity?: number | null;
  note?: string | null;
  enteredByKind?: string | null;
  claimedAt?: string | null;
  /** The stay period (accommodations), as YYYY-MM-DD. Null on every other kind. */
  startsOn?: string | null;
  endsOn?: string | null;
  /** Nights the stay covers. ⚠ 0 means the dates on file could not be placed on a calendar. */
  nights?: number | null;
  /** price x quantity, heads or nights depending on the kind. Null when that multiplier is unknown. */
  amount?: number | null;
}

/** One signup item, with what has actually been claimed against it. */
export interface SignupStatTarget {
  targetId?: string | null;
  name?: string | null;
  categoryId?: string | null;
  /** ⚠ NULL means UNLIMITED, not zero — never render it as "0 offered". */
  quantityOffered?: number | null;
  maxQuantityPerGuest?: number | null;
  claimedQuantity?: number | null;
  signupCount?: number | null;
  guestCount?: number | null;
  /** Null when the item is unlimited. */
  remainingQuantity?: number | null;
  isFull?: boolean | null;
  isUntouched?: boolean | null;
  orderNo?: number | null;
  status?: number | null;
  /** False when the item is no longer on the list but guests are still signed up for it. */
  isOffered?: boolean | null;
  /** Null when the item is not priced. */
  price?: number | null;
  currencyCode?: string | null;
  /** The host's own free-text unit label. Shown, never parsed. */
  priceUnit?: string | null;
  claimedAmount?: number | null;
  /** PEOPLE committed here, each party counted once. The headline for a participation kind. */
  claimedHeads?: number | null;
  /** Guests who upvoted it. Interest, which is a different signal from commitment. */
  upvoteCount?: number | null;
  /** Nights booked here across every stay. */
  claimedNights?: number | null;
  /** Stays here with no usable dates — rooms the host still has to find. */
  unplaceableStays?: number | null;
  /** Places only. */
  streetAddress?: string | null;
  distanceMeters?: number | null;
  groupBookingCode?: string | null;
  claims?: SignupClaim[] | null;
}

/**
 * How many people are staying on ONE date. ⚠ The check-out day is not a night.
 *
 * ⚠ `stays` counts BOOKINGS, not distinct people — a guest signed up to two places over the same
 * night appears in both. `SignupStats.doubleBookedGuests` is how many guests that is.
 */
export interface SignupNight {
  date?: string | null;
  stays?: number | null;
  heads?: number | null;
}

export interface SignupStats {
  eventId?: string | null;
  targetKind?: string | null;
  audience?: string | null;
  includeEmptyTargets?: boolean | null;
  audienceGuests?: number | null;
  audienceHeads?: number | null;
  claimantGuests?: number | null;
  targetCount?: number | null;
  offeredCount?: number | null;
  untouchedCount?: number | null;
  fullCount?: number | null;
  unlistedCount?: number | null;
  totalOffered?: number | null;
  totalClaimed?: number | null;
  totalSignups?: number | null;
  claimsTruncated?: boolean | null;
  targets?: SignupStatTarget[] | null;
  /** True when at least one item is priced — the page shows money only then. */
  hasPricedItems?: boolean | null;
  totalsByCurrency?: SignupAmount[] | null;
  participation?: string | null;
  includeGuestRoster?: boolean | null;
  guests?: SignupGuest[] | null;
  guestsSignedUp?: number | null;
  guestsNotSignedUp?: number | null;
  guestRosterTruncated?: boolean | null;
  /** What this kind counts: quantity | participation. ⚠ Read it, never infer it from the figures. */
  measure?: string | null;
  /** What an amount is price x: quantity | heads | nights. Chosen by kind, never from priceUnit. */
  priceBasis?: string | null;
  /** True when this kind carries check-in / check-out dates. */
  hasStayDates?: boolean | null;
  totalHeads?: number | null;
  totalUpvotes?: number | null;
  /** True when this kind can be upvoted, so 0 votes is distinguishable from no upvote pill at all. */
  isUpvotable?: boolean | null;
  totalNights?: number | null;
  /** Beds needed per date. ⚠ Built from the claim roster, so claimsTruncated applies to it. */
  nightOccupancy?: SignupNight[] | null;
  peakNightDate?: string | null;
  peakNightHeads?: number | null;
  /** Stays with no usable dates. Counted, never dropped — each is still a room to find. */
  unplaceableStays?: number | null;
  /**
   * Guests booked into more than one place on the same night. Counted once per stay in the nightly
   * heads, never silently deduplicated — the server cannot know which booking is the real one.
   */
  doubleBookedGuests?: number | null;
}

// =============================================================================
// Service
// =============================================================================

export class SignupRecordService {
  /**
   * The signup-list report (#44). Both switches have server-side defaults; `targetKind` is
   * injected here because this service IS the signup-item view of the unified table.
   */
  static async stats(
    eventId: string,
    options?: {
      /** Which kind of sign-up to report on. Defaults to signup items, this service's own subject. */
      targetKind?: string;
      audience?: string;
      /** With `audience: by_status` — count only guests with these RSVP statuses. */
      rsvpStatuses?: string[];
      includeEmptyTargets?: boolean;
      participation?: string;
      /** The by-guest view. Left false, the server does not read the roster at all. */
      includeGuestRoster?: boolean;
    },
  ): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_STATS_QUERY, {
      params: {
        eventId,
        // ⚠ No silent fallback for an UNKNOWN kind: the server refuses anything outside its
        // whitelist, which is the behaviour we want. Defaulting a typo to signup items here would
        // answer a question about hotels with a report about salads.
        targetKind: options?.targetKind ?? SIGNUP_ITEM_TARGET_KIND,
        ...signupReportCountParams(options?.audience ?? SIGNUP_REPORT_AUDIENCE.HOLDING_PLACE, options?.rsvpStatuses),
        includeEmptyTargets: options?.includeEmptyTargets ?? true,
        participation: options?.participation ?? SIGNUP_REPORT_PARTICIPATION.ALL,
        includeGuestRoster: options?.includeGuestRoster ?? false,
      },
    });
  }

  static async list(filter?: SignupRecordFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(RECORD_LIST_QUERY, {
      filter: toApiFilter(filter),
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(RECORD_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(RECORD_GET_MANY_QUERY, { ids });
  }

  static async create(params: SignupRecordInput): Promise<OpResult> {
    return ApiService.executeMutation(RECORD_CREATE_MUTATION, { params: toApiInput(params) });
  }

  static async update(where: SignupRecordWhereInput, params: SignupRecordInput): Promise<OpResult> {
    return ApiService.executeMutation(RECORD_UPDATE_MUTATION, { where, params: toApiInput(params) });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(RECORD_REMOVE_MUTATION, { where: { id } });
  }

  static async bulkUpdate(ids: string[], params: SignupRecordBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(RECORD_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(RECORD_REMOVE_MUTATION, { where: { id: ids } });
  }
}
